import { GridItemChangeEvent } from '@progress/kendo-react-grid';
import { isEmpty } from 'lodash';
import {
  CombineLineItemColumns,
  ResultingLineItemColumns,
} from 'src/assets/static/invoice_details/CombineLineItemColumns';
import GridWrapper from 'src/components/GridWrapper';

import { DialogActionsBar } from '@progress/kendo-react-dialogs';
import { useCallback, useMemo } from 'react';
import Button from 'src/components/design-system/Button';
import { InvoiceLineItem } from 'src/services/useInvoiceLineItemsApi';
import { SetState } from 'src/types/generic';
import { useContextSelector } from 'use-context-selector';
import { InvoiceContext } from './InvoiceDetailsContext';

type CombineLineItemProps = {
  combinableLineItems: InvoiceLineItem[];
  close: (success: boolean) => () => void;
  data: InvoiceLineItem[];
  setData: SetState<InvoiceLineItem[]>;
  itemWithLeastItemNumber: InvoiceLineItem;
  isPOQtyOfItemWithLeastItemNumZero: boolean;
};

const initialReceiptDataState: any = {
  skip: 0,
  take: 2,
  skipCalculatingTotalRecordsCount: false,
  sort: [
    {
      field: 'poQty',
      dir: 'asc',
    },
  ],
  filters: [],
};

function CombineLineItemsWizard({
  combinableLineItems,
  close,
  data,
  setData,
  isPOQtyOfItemWithLeastItemNumZero,
}: CombineLineItemProps) {
  const title = useContextSelector(InvoiceContext, (value) => value.title);

  const isAnyRequiredFieldEmpty = useMemo(() => {
    if (!data?.[0]) return true;
    const { productItemNum, description, poGlCd, uom } = data?.[0];
    return !!(isEmpty(productItemNum) || isEmpty(description) || isEmpty(poGlCd) || isEmpty(uom));
  }, [data]);

  const itemChange = useCallback(
    (e: GridItemChangeEvent) => {
      const { field = '', value } = e;
      const { rowId } = e.dataItem;
      setData((prev) => [
        ...prev?.map((item: any) => {
          if (item.rowId === rowId) {
            item[field || ''] = value;
          }
          return item;
        }),
      ]);
    },
    [setData]
  );
  const onApplyClick = useCallback(() => close(true)(), [close]);

  return (
    <>
      <div className="mb-2">
        Fill the final values for the two items you wish to combine and click "Apply" or click
        "Cancel" to close the dialog
      </div>
      <div>Resulting {title} Item</div>
      <GridWrapper
        columns={ResultingLineItemColumns}
        dataSource={data}
        onItemChange={itemChange}
        dataState={initialReceiptDataState}
        sortable
        total={1}
        DataItemKey="rowId"
        pageable={false}
        tableTextId="lineItems"
      />
      <div className="mt-2">{title} Items to Combine</div>
      <GridWrapper
        resizable
        columns={CombineLineItemColumns}
        dataSource={combinableLineItems}
        dataState={initialReceiptDataState}
        sortable
        total={2}
        DataItemKey="rowId"
        pageable={false}
        tableTextId="lineItems"
      />
      <DialogActionsBar>
        <div className="flex justify-end gap-2">
          <Button color="success" disabled={isAnyRequiredFieldEmpty} onClick={onApplyClick}>
            Apply
          </Button>
          <Button color="info" onClick={close(false)}>
            Cancel
          </Button>
        </div>
      </DialogActionsBar>
    </>
  );
}

export default CombineLineItemsWizard;
